import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Users, 
  Building, 
  GraduationCap, 
  DollarSign, 
  TrendingUp, 
  FileText,
  Plus,
  Eye,
  Edit,
  MoreHorizontal,
  Calendar,
  Activity,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import StatCard from '../../components/dashboard/StatCard';
import adminService, { AdminStatistics } from '../../services/admin.service';

interface DashboardStats {
  totalUsers: number;
  totalTrainees: number;
  totalTrainingCenters: number;
  totalConsultants: number;
  totalTrainings: number;
  totalServicesPublished: number;
  totalFormationsPublished: number;
  totalServiceViews: number;
  totalTrainingViews: number;
  totalRevenue: number;
  activeSubscriptions: number;
  totalReservations: number;
  totalTraineeSubscriptions: number;
}

interface RecentActivity {
  id: string;
  action: string;
  user: string;
  date: string;
  status: string;
}

const AdminDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalTrainees: 0,
    totalTrainingCenters: 0,
    totalConsultants: 0,
    totalTrainings: 0,
    totalServicesPublished: 0,
    totalFormationsPublished: 0,
    totalServiceViews: 0,
    totalTrainingViews: 0,
    totalRevenue: 0,
    activeSubscriptions: 0,
    totalReservations: 0,
    totalTraineeSubscriptions: 0
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotalPages, setActivityTotalPages] = useState(1);
  const activitiesPerPage = 5;

  const formatActivityDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'À l\'instant';
    } else if (diffMins < 60) {
      return `Il y a ${diffMins} min${diffMins > 1 ? 's' : ''}`;
    } else if (diffHours < 24) {
      return `Il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
    } else if (diffDays < 7) {
      return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
    } else {
      return date.toLocaleDateString('fr-DZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  };

  const loadRecentActivity = async () => {
    try {
      const activityData = await adminService.getRecentActivity(
        activitiesPerPage,
        (activityPage - 1) * activitiesPerPage
      );
      setRecentActivity(activityData.activities.map((activity) => ({
        id: activity.id,
        action: activity.action,
        user: activity.user,
        date: formatActivityDate(activity.date),
        status: activity.status
      })));
      setActivityTotalPages(activityData.totalPages);
    } catch (error) {
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      loadRecentActivity();
    }
  }, [activityPage]);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      // Use the same statistics service as ReportsPage
      const statsData = await adminService.getStatistics('all');

      setStats({
        totalUsers: statsData.totalUsers || 0,
        totalTrainees: statsData.totalTrainees || 0,
        totalTrainingCenters: statsData.totalTrainingCenters || 0,
        totalConsultants: statsData.totalConsultants || 0,
        totalTrainings: 0, // Not used anymore, split into formations and services
        totalServicesPublished: statsData.totalServicesPublished || 0,
        totalFormationsPublished: statsData.totalFormationsPublished || 0,
        totalServiceViews: statsData.totalServiceViews || 0,
        totalTrainingViews: statsData.totalTrainingViews || 0,
        totalRevenue: 0, // Revenue calculation would need to be added separately
        activeSubscriptions: statsData.activeSubscriptions || 0, // Use actual active subscriptions from database
        totalReservations: statsData.totalReservations || 0,
        totalTraineeSubscriptions: Number(statsData.totalTraineeSubscriptions) || 0
      });

      // Load initial recent activity
      await loadRecentActivity();
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-brand-primary/5 via-brand-mint/10 to-brand-secondary/5 rounded-xl p-4 sm:p-6 border border-brand-primary/20">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              {t('admin.dashboard.title', 'Administrator dashboard')} 👨‍💼
            </h2>
            <p className="text-gray-600 text-sm sm:text-base">
              {t('admin.dashboard.subtitle', 'Manage your PHINLEX platform and monitor performance')}
            </p>
          </div>
          <Link
            to="/admin/users"
            className="inline-flex items-center justify-center px-4 py-2 bg-brand-primary text-white font-medium rounded-lg hover:opacity-90 transition-all shadow-md text-sm sm:text-base"
          >
            <Plus className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">{t('admin.dashboard.manageUsers', 'Manage users')}</span>
            <span className="sm:hidden">{t('admin.dashboard.users', 'Users')}</span>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
        <StatCard
          title={t('admin.dashboard.stats.totalUsers', 'Total users')}
          value={new Intl.NumberFormat('fr-DZ').format(stats.totalUsers)}
          changeType="neutral"
          icon={Users}
          color="brand-primary"
        />
        <StatCard
          title="Trainees"
          value={new Intl.NumberFormat('fr-DZ').format(stats.totalTrainees || 0)}
          changeType="neutral"
          icon={Users}
          color="green"
        />
        <StatCard
          title={t('admin.dashboard.stats.trainingCenters', 'Training centers')}
          value={new Intl.NumberFormat('fr-DZ').format(stats.totalTrainingCenters)}
          changeType="neutral"
          icon={Building}
          color="blue"
        />
        <StatCard
          title="Consultants"
          value={new Intl.NumberFormat('fr-DZ').format(stats.totalConsultants || 0)}
          changeType="neutral"
          icon={Building}
          color="purple"
        />
        <StatCard
          title="Formations"
          value={new Intl.NumberFormat('fr-DZ').format(stats.totalFormationsPublished || 0)}
          changeType="neutral"
          icon={GraduationCap}
          color="orange"
        />
        <StatCard
          title="Services de consultation"
          value={new Intl.NumberFormat('fr-DZ').format(stats.totalServicesPublished || 0)}
          changeType="neutral"
          icon={FileText}
          color="brand-secondary"
        />
        <StatCard
          title="Vues totales"
          value={new Intl.NumberFormat('fr-DZ').format((stats.totalServiceViews || 0) + (stats.totalTrainingViews || 0))}
          changeType="neutral"
          icon={Eye}
          color="brand-mint"
        />
        <StatCard
          title={t('admin.dashboard.stats.activeSubscriptions', 'Active subscriptions')}
          value={new Intl.NumberFormat('fr-DZ').format(stats.activeSubscriptions)}
          changeType="neutral"
          icon={TrendingUp}
          color="brand-accent"
        />
        <StatCard
          title="Réservations"
          value={new Intl.NumberFormat('fr-DZ').format(stats.totalReservations || 0)}
          changeType="neutral"
          icon={Calendar}
          color="brand-secondary"
        />
        <StatCard
          title="Inscriptions formations"
          value={new Intl.NumberFormat('fr-DZ').format(stats.totalTraineeSubscriptions || 0)}
          changeType="neutral"
          icon={Users}
          color="brand-primary"
        />
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h3 className="text-lg font-semibold text-gray-900">{t('admin.dashboard.recentActivity.title', 'Recent activity')}</h3>
         
          </div>
        </div>
        <div className="p-4 sm:p-6">
          {recentActivity.length > 0 ? (
            <div className="space-y-3 sm:space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 bg-gray-50 rounded-lg gap-3 sm:gap-0">
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-brand-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Activity className="h-4 w-4 sm:h-5 sm:w-5 text-brand-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{activity.action}</p>
                      <p className="text-xs sm:text-sm text-gray-500 truncate">{activity.user}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end space-x-2 sm:space-x-4">
                    <span className="text-xs sm:text-sm text-gray-500">{activity.date}</span>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        activity.status === 'approved' || activity.status === 'completed' || activity.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {activity.status === 'approved' && t('common.status.approved', 'Approved')}
                        {activity.status === 'completed' && t('common.status.completed', 'Completed')}
                        {activity.status === 'active' && t('common.status.active', 'Active')}
                        {activity.status === 'pending' && t('common.status.pending', 'Pending')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Aucune activité récente</p>
            </div>
          )}

          {/* Pagination */}
          {activityTotalPages > 1 && (
            <div className="flex items-center justify-center mt-6 sm:mt-8 space-x-1 sm:space-x-2 overflow-x-auto pb-2">
              <button
                onClick={() => setActivityPage(prev => Math.max(1, prev - 1))}
                disabled={activityPage === 1}
                className="flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4 mr-0.5 sm:mr-1" />
                <span className="hidden sm:inline">Précédent</span>
                <span className="sm:hidden">Préc.</span>
              </button>
              
              <div className="flex items-center space-x-0.5 sm:space-x-1">
                {Array.from({ length: Math.min(5, activityTotalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setActivityPage(pageNum)}
                      className={`px-2.5 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-lg ${
                        activityPage === pageNum
                          ? 'bg-brand-primary text-white'
                          : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setActivityPage(prev => Math.min(activityTotalPages, prev + 1))}
                disabled={activityPage === activityTotalPages}
                className="flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <span className="hidden sm:inline">Suivant</span>
                <span className="sm:hidden">Suiv.</span>
                <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 ml-0.5 sm:ml-1" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link
          to="/admin/users"
          className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-brand-mint/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-brand-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-gray-900 text-sm sm:text-base">Gérer les utilisateurs</h4>
              <p className="text-xs sm:text-sm text-gray-500">Voir et modifier les comptes</p>
            </div>
          </div>
        </Link>

        <Link
          to="/admin/training-centers"
          className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Building className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-gray-900 text-sm sm:text-base">Centres de formation</h4>
              <p className="text-xs sm:text-sm text-gray-500">Approuver et gérer</p>
            </div>
          </div>
        </Link>

        <Link
          to="/admin/trainings?filter=pending"
          className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <GraduationCap className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-gray-900 text-sm sm:text-base">Formations en attente</h4>
              <p className="text-xs sm:text-sm text-gray-500">Approuver les formations</p>
            </div>
          </div>
        </Link>

        <Link
          to="/admin/reports"
          className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-gray-900 text-sm sm:text-base">Rapports</h4>
              <p className="text-xs sm:text-sm text-gray-500">Analyses et statistiques</p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
